import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, Profile } from '../utils/supabase';
import { activityService } from '../services/activity.service';

export const RESET_PASSWORD_PATH = '/restablecer-contrasena';

// Tipos del contexto
interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  /** El perfil del usuario actual ya se pidió (llegó o falló). `ProtectedRoute` espera esto para decidir el rol. */
  profileReady: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  // Id del usuario cuyo perfil ya se pidió. «Perfil listo» se deriva de él en el mismo render en que cambia `user`:
  // con un booleano que un efecto pone en false, el render donde llega la sesión veía «listo» y mandaba al chat (16-10).
  const [profileLoadedFor, setProfileLoadedFor] = useState<string | null>(null);
  const profileReady = !user || profileLoadedFor === user.id;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Obtener sesión inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Si Supabase devuelve el enlace de recuperación a otra URL (p. ej. la Site URL),
      // se lleva al usuario a fijar su contraseña en vez de dejarlo dentro sin cambiarla.
      if (event === 'PASSWORD_RECOVERY' && window.location.pathname !== RESET_PASSWORD_PATH) {
        window.location.assign(RESET_PASSWORD_PATH);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Función de login
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Log successful login activity
    if (!error) {
      try {
        await activityService.logActivity('login', {
          email,
          timestamp: new Date().toISOString(),
        });
      } catch (activityError) {
        // Don't fail login if activity logging fails
        console.error('Failed to log activity:', activityError);
      }
    }

    return { error };
  };

  // Función de registro
  const signUp = async (email: string, password: string, name: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
        },
      },
    });

    // Profile is automatically created by database trigger (handle_new_user)
    // No manual insert needed - this prevents duplicate key errors

    return { error };
  };

  // Función de logout
  const signOut = async () => {
    // Log logout activity before signing out
    try {
      await activityService.logActivity('logout');
    } catch (error) {
      console.error('Failed to log logout activity:', error);
    }

    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoadedFor(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
      .then(({ data }) => data, () => null)
      .then(data => {
        if (cancelled) return;
        setProfile(data);
        setProfileLoadedFor(user.id);
      });
    return () => { cancelled = true; };
  }, [user]);

  const value: AuthContextType = {
    user,
    session,
    profile,
    profileReady,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook personalizado para usar el contexto
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}

export default AuthContext;
